/**
 * The assistant: the app's home screen. The owner asks by text or voice; answers come from the
 * `assistant` Edge Function, which reads only and turns spend changes into cards to approve.
 */
import { FunctionsFetchError, FunctionsHttpError } from '@supabase/supabase-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AnswerText,
  Cards,
  ListIcon,
  MicIcon,
  PlusIcon,
  SendIcon,
  SpeakerIcon,
  StopIcon,
} from '../chatParts.tsx';
import { useApp } from '../context.tsx';
import {
  cardKey,
  chatErrorKey,
  composerRows,
  isTrial,
  toolsNotLinked,
  trialBannerFor,
  type ChatMessage,
} from '../lib/chat.ts';
import { formatDateTime } from '../lib/format.ts';
import type { MessageKey } from '../lib/i18n.ts';
import { speakable } from '../lib/markdown.ts';
import { supabase } from '../supabase.ts';
import { speak, stopSpeaking, useDictation } from '../useVoice.ts';

const SPEAK_KEY = 'asparagus-ads.speak-replies';

interface Conversation {
  readonly id: string;
  readonly title: string | null;
  readonly updatedAt: string;
}

interface AssistantReply {
  readonly conversationId: string;
  readonly title: string | null;
  readonly message: {
    readonly id: number;
    readonly createdAt: string;
    readonly content: string;
    readonly dataSource: string;
    readonly googleAdsNotLinked?: boolean;
    readonly cards: unknown;
  };
}

function storedSpeak(): boolean {
  try {
    return window.localStorage.getItem(SPEAK_KEY) === 'on';
  } catch {
    return false;
  }
}

/**
 * A failed call's error code ("network" when it never answered) and, when the server had already
 * opened the conversation, its id — so a retry continues in the same conversation.
 */
async function failureOf(error: unknown): Promise<{ code: string; conversationId: string | null }> {
  if (error instanceof FunctionsFetchError) return { code: 'network', conversationId: null };
  if (error instanceof FunctionsHttpError) {
    try {
      const body = (await (error.context as Response).json()) as {
        error?: { code?: string };
        conversationId?: string | null;
      };
      return { code: body.error?.code ?? 'unknown', conversationId: body.conversationId ?? null };
    } catch {
      return { code: 'unknown', conversationId: null };
    }
  }
  return { code: 'unknown', conversationId: null };
}

/** Which decision cards of these answers were already saved (proposal cards read their own row). */
async function savedCards(messages: readonly ChatMessage[]): Promise<Set<string>> {
  const ids = messages.flatMap((m) =>
    m.role === 'assistant' && m.messageId !== null ? [m.messageId] : [],
  );
  if (ids.length === 0) return new Set();
  const { data } = await supabase
    .from('decisions')
    .select('source_message_id, source_card')
    .in('source_message_id', ids);
  return new Set(
    (data ?? []).flatMap((d) =>
      d.source_message_id !== null && d.source_card !== null
        ? [cardKey(d.source_message_id, d.source_card)]
        : [],
    ),
  );
}

async function fetchConversations(): Promise<Conversation[]> {
  const { data } = await supabase
    .from('conversations')
    .select('id, title, updated_at')
    .not('title', 'is', null) // a conversation gets its title with its first answer
    .order('updated_at', { ascending: false })
    .limit(30);
  return (data ?? []).map((c) => ({ id: c.id, title: c.title, updatedAt: c.updated_at }));
}

export function Chat() {
  const { t, language, role } = useApp();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [dictated, setDictated] = useState(false);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<{
    key: MessageKey;
    question: string;
    inputMode: 'text' | 'voice';
    localKey: string;
  } | null>(null);
  const [saved, setSaved] = useState<ReadonlySet<string>>(new Set());
  // Which conversation is on screen; a reply for an earlier view is not shown in this one.
  const view = useRef(0);
  const sentCount = useRef(0);
  const [showHistory, setShowHistory] = useState(false);
  const [speakReplies, setSpeakReplies] = useState(storedSpeak);
  const endRef = useRef<HTMLDivElement>(null);

  const onDictation = useCallback((text: string) => {
    setDraft(text);
    setDictated(true);
  }, []);
  const voice = useDictation(language, onDictation);

  const [listVersion, setListVersion] = useState(0);
  const refreshConversations = useCallback(() => {
    setListVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    if (role !== 'owner') return;
    let current = true;
    void fetchConversations().then((list) => {
      if (current) setConversations(list);
    });
    return () => {
      current = false;
    };
  }, [role, listVersion]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, pending]);

  useEffect(() => {
    try {
      window.localStorage.setItem(SPEAK_KEY, speakReplies ? 'on' : 'off');
    } catch {
      // Without storage the choice lasts for this visit.
    }
    if (!speakReplies) stopSpeaking();
  }, [speakReplies]);

  const open = async (conversation: Conversation) => {
    const current = ++view.current;
    setShowHistory(false);
    setFailure(null);
    setConversationId(conversation.id);
    setTitle(conversation.title);
    const { data } = await supabase
      .from('conversation_messages')
      .select('id, role, content, data_source, tools, input_mode, cards, created_at')
      .eq('conversation_id', conversation.id)
      .order('id', { ascending: true });
    const loaded: ChatMessage[] = (data ?? []).map((m) => ({
      key: String(m.id),
      messageId: m.id,
      role: m.role === 'assistant' ? 'assistant' : 'user',
      content: m.content,
      createdAt: m.created_at,
      dataSource: m.data_source,
      notLinked: toolsNotLinked(m.tools),
      inputMode: m.input_mode,
      cards: m.cards,
    }));
    const saved = await savedCards(loaded);
    if (current !== view.current) return;
    setMessages(loaded);
    setSaved(saved);
  };

  const startNew = () => {
    view.current++;
    stopSpeaking();
    setShowHistory(false);
    setConversationId(null);
    setTitle(null);
    setMessages([]);
    setSaved(new Set());
    setFailure(null);
  };

  const send = async (question: string, inputMode: 'text' | 'voice') => {
    const text = question.trim();
    if (text === '' || pending) return;
    if (voice.listening) voice.stop();
    const current = view.current;
    const localKey = `local-${String(++sentCount.current)}`;
    setFailure(null);
    setDraft('');
    setDictated(false);
    setPending(true);
    setMessages((m) => [
      ...m,
      {
        key: localKey,
        messageId: null,
        role: 'user',
        content: text,
        createdAt: new Date().toISOString(),
        dataSource: null,
        notLinked: null,
        inputMode,
        cards: [],
      },
    ]);
    const result = await supabase.functions.invoke('assistant', {
      body: { conversationId, message: text, inputMode },
    });
    const data = result.data as AssistantReply | null;
    const error: unknown = result.error;
    setPending(false);
    if (current !== view.current) {
      // The owner moved to another conversation meanwhile; the answer is in the history list.
      refreshConversations();
      return;
    }
    if (error || !data) {
      const failed = await failureOf(error);
      if (failed.conversationId) setConversationId(failed.conversationId);
      setFailure({ key: chatErrorKey(failed.code), question: text, inputMode, localKey });
      return;
    }
    setConversationId(data.conversationId);
    if (data.title) setTitle(data.title);
    setMessages((m) => [
      ...m,
      {
        key: String(data.message.id),
        messageId: data.message.id,
        role: 'assistant',
        content: data.message.content,
        createdAt: data.message.createdAt,
        dataSource: data.message.dataSource,
        notLinked: data.message.googleAdsNotLinked ?? null,
        inputMode: null,
        cards: data.message.cards,
      },
    ]);
    if (speakReplies) speak(speakable(data.message.content), language);
    refreshConversations();
  };

  const retry = () => {
    if (failure === null) return;
    // The failed question was not stored; it is sent again in place of its bubble.
    setMessages((m) => m.filter((x) => x.key !== failure.localKey));
    void send(failure.question, failure.inputMode);
  };

  if (role !== 'owner') return <p className="notice">{t('chatOwnerOnly')}</p>;

  const suggestions: MessageKey[] = [
    'chatSuggest1',
    'chatSuggest2',
    'chatSuggest3',
    'chatSuggest4',
  ];

  const trialBanner = trialBannerFor(messages);
  return (
    <div className="chat">
      <header className="chat-head">
        <h1>{title ?? t('navChat')}</h1>
        <div className="chat-head-actions">
          <button
            type="button"
            className="icon-button"
            aria-pressed={speakReplies}
            aria-label={speakReplies ? t('chatSpeakOn') : t('chatSpeakOff')}
            title={speakReplies ? t('chatSpeakOn') : t('chatSpeakOff')}
            onClick={() => {
              setSpeakReplies((s) => !s);
            }}
          >
            <SpeakerIcon />
          </button>
          <button
            type="button"
            className="icon-button"
            aria-expanded={showHistory}
            aria-label={t('chatHistory')}
            title={t('chatHistory')}
            onClick={() => {
              setShowHistory((s) => !s);
            }}
          >
            <ListIcon />
          </button>
          <button
            type="button"
            className="icon-button"
            aria-label={t('chatNew')}
            title={t('chatNew')}
            onClick={startNew}
          >
            <PlusIcon />
          </button>
        </div>
      </header>

      {showHistory && (
        <section aria-label={t('chatHistory')}>
          {conversations.length === 0 ? (
            <p className="muted">{t('chatNoHistory')}</p>
          ) : (
            <ul className="conversations">
              {conversations.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    aria-current={c.id === conversationId}
                    onClick={() => void open(c)}
                  >
                    <span>{c.title ?? t('chatNew')}</span>
                    <span className="small muted">{formatDateTime(c.updatedAt, language)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {trialBanner !== null && (
        <p className="trial-banner" role="status">
          {t(trialBanner)}
        </p>
      )}

      <div className="messages" aria-live="polite">
        {messages.length === 0 && !pending && (
          <section className="welcome">
            <h2>{t('chatWelcomeTitle')}</h2>
            <p>{t('chatWelcomeText')}</p>
            <div className="suggestions">
              {suggestions.map((key) => (
                <button
                  key={key}
                  type="button"
                  className="suggestion"
                  onClick={() => void send(t(key), 'text')}
                >
                  {t(key)}
                </button>
              ))}
            </div>
          </section>
        )}
        {messages.map((m) =>
          m.role === 'user' ? (
            <div key={m.key} className="bubble user">
              <p className="pre" style={{ margin: 0 }}>
                {m.content}
              </p>
              {m.inputMode === 'voice' && <div className="meta">{t('chatViaVoice')}</div>}
            </div>
          ) : (
            <article key={m.key} className="bubble assistant">
              <AnswerText text={m.content} tableLabel={t('navChat')} />
              <Cards raw={m.cards} messageId={m.messageId} saved={saved} />
              <div className="meta">
                <span>{formatDateTime(m.createdAt, language)}</span>
                {isTrial(m.dataSource) && <span className="badge">{t('chatTrialShort')}</span>}
              </div>
            </article>
          ),
        )}
        {pending && (
          <div className="bubble assistant" aria-label={t('chatThinking')}>
            <span className="typing" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
            <span className="small muted">{t('chatThinking')}</span>
          </div>
        )}
        {failure && (
          <div role="alert" className="notice error">
            <p>{t(failure.key)}</p>
            {failure.key !== 'chatErrNotOwner' && failure.key !== 'chatErrNotConfigured' && (
              <button type="button" className="button quiet" onClick={retry}>
                {t('chatRetry')}
              </button>
            )}
          </div>
        )}
        <div ref={endRef} />
      </div>

      {voice.listening && <p className="small muted">{t('chatListening')}</p>}
      {voice.problem && (
        <p className="small error-text">
          {t(voice.problem === 'denied' ? 'chatVoiceDenied' : 'chatVoiceUnsupported')}
        </p>
      )}

      <div className="composer-dock">
        <form
          className="composer"
          onSubmit={(e) => {
            e.preventDefault();
            void send(draft, dictated ? 'voice' : 'text');
          }}
        >
          {voice.supported && (
            <button
              type="button"
              className={voice.listening ? 'mic-button listening' : 'mic-button'}
              aria-label={voice.listening ? t('chatMicStop') : t('chatMicStart')}
              title={voice.listening ? t('chatMicStop') : t('chatMicStart')}
              onClick={() => {
                if (voice.listening) voice.stop();
                else voice.start(draft);
              }}
            >
              {voice.listening ? <StopIcon /> : <MicIcon />}
            </button>
          )}
          <textarea
            value={draft}
            rows={composerRows(draft)}
            placeholder={t('chatPlaceholder')}
            aria-label={t('chatPlaceholder')}
            onChange={(e) => {
              setDraft(e.target.value);
            }}
            onKeyDown={(e) => {
              // Enter sends on a keyboard; Shift+Enter adds a line. Phones send with the button.
              if (
                e.key === 'Enter' &&
                !e.shiftKey &&
                !e.nativeEvent.isComposing &&
                window.matchMedia('(hover: hover)').matches
              ) {
                e.preventDefault();
                void send(draft, dictated ? 'voice' : 'text');
              }
            }}
          />
          <button
            type="submit"
            className="send-button"
            disabled={draft.trim() === '' || pending}
            aria-label={t('chatSend')}
          >
            <SendIcon rtl={language === 'ar'} />
          </button>
        </form>
      </div>
    </div>
  );
}
